import React from "react";
import MovieEmbed from "@/components/MovieEmbed";

/**
 * Minimal BBCode → React renderer with a whitelisted tag set.
 * - Safe by default: URLs restricted to http/https (and protocol-relative),
 *   image URLs same restriction, colors validated as hex/named.
 * - Tags supported: [b][i][u][s][url][img][quote][code][color][size]
 *                   [spoiler][ul][li]
 * - Unknown tags are left as plain text.
 * - Newlines preserved (rendered via `whitespace-pre-wrap` on the wrapper).
 */

const TAG_OPEN = /\[([a-zA-Z][a-zA-Z0-9]{0,9})(?:=([^\]]{0,200}))?\]/;
const TAG_CLOSE_OF = (name) => new RegExp(`\\[\\/${name}\\]`, "i");
const NAMED_COLORS = new Set([
  "red", "green", "blue", "orange", "purple", "pink", "white", "black",
  "yellow", "cyan", "magenta", "gray", "grey", "silver", "gold", "lime",
]);

function safeUrl(u) {
  if (!u) return null;
  u = String(u).trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (/^\/\//.test(u)) return `https:${u}`;
  return null;
}

function safeColor(c) {
  if (!c) return null;
  c = String(c).trim().toLowerCase();
  if (/^#[0-9a-f]{3}([0-9a-f]{3})?$/.test(c)) return c;
  if (NAMED_COLORS.has(c)) return c;
  return null;
}

function safeSize(s) {
  const n = parseInt(s, 10);
  if (Number.isNaN(n)) return null;
  return Math.max(10, Math.min(28, n));
}

/** Render a node tree element to React. */
function renderNode(node, keyPrefix = "") {
  if (typeof node === "string") return node;
  const { tag, arg, children } = node;
  const kids = children.map((c, i) => (
    <React.Fragment key={`${keyPrefix}-${i}`}>{renderNode(c, `${keyPrefix}-${i}`)}</React.Fragment>
  ));
  switch (tag) {
    case "b":
      return <strong className="font-bold">{kids}</strong>;
    case "i":
      return <em className="italic">{kids}</em>;
    case "u":
      return <span className="underline">{kids}</span>;
    case "s":
      return <s className="line-through opacity-80">{kids}</s>;
    case "url": {
      const flat = node.children.map((c) => (typeof c === "string" ? c : "")).join("");
      const href = safeUrl(arg || flat);
      if (!href) return <>{kids}</>;
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-[var(--tt-primary)] underline break-words">
          {arg ? kids : href}
        </a>
      );
    }
    case "img": {
      const flat = node.children.map((c) => (typeof c === "string" ? c : "")).join("");
      const src = safeUrl(flat);
      if (!src) return null;
      return <img src={src} alt="" className="inline-block max-h-80 max-w-full rounded my-1 border border-[var(--tt-border)]" />;
    }
    case "quote":
      return (
        <blockquote className="my-2 pl-3 border-l-4 border-[var(--tt-primary)]/60 bg-white/[0.03] rounded-r py-2 pr-2">
          {arg && (
            <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--tt-primary)] mb-1">
              {arg} wrote:
            </div>
          )}
          <div className="text-white/85 italic">{kids}</div>
        </blockquote>
      );
    case "code":
      return (
        <pre className="my-2 bg-black/40 border border-[var(--tt-border)] rounded p-3 overflow-x-auto text-[12px] font-mono-tt whitespace-pre">
          {kids}
        </pre>
      );
    case "color": {
      const c = safeColor(arg);
      if (!c) return <>{kids}</>;
      return <span style={{ color: c }}>{kids}</span>;
    }
    case "size": {
      const px = safeSize(arg);
      if (!px) return <>{kids}</>;
      return <span style={{ fontSize: `${px}px`, lineHeight: 1.4 }}>{kids}</span>;
    }
    case "spoiler":
      return (
        <span
          onClick={(e) => e.currentTarget.classList.toggle("bg-transparent")}
          className="bg-[#222] hover:bg-[#2a2a2a] cursor-pointer px-1 rounded transition-colors"
          title="Click to reveal"
        >
          {kids}
        </span>
      );
    case "ul":
      return <ul className="list-disc list-inside my-1 space-y-0.5">{kids}</ul>;
    case "li":
      return <li>{kids}</li>;
    case "movie":
    case "tv":
    case "title": {
      const flat = node.children.map((c) => (typeof c === "string" ? c : "")).join("").trim();
      return <MovieEmbed arg={arg} query={flat} mediaType={tag === "title" ? "title" : tag} />;
    }
    default:
      return <>{kids}</>;
  }
}

/** Parse a substring into a list of nodes (strings or {tag, arg, children}). */
function parse(text) {
  const root = { tag: "root", arg: null, children: [] };
  const stack = [root];
  let i = 0;
  while (i < text.length) {
    const slice = text.slice(i);
    const m = slice.match(TAG_OPEN);
    if (!m) {
      stack[stack.length - 1].children.push(slice);
      break;
    }
    if (m.index > 0) {
      stack[stack.length - 1].children.push(slice.slice(0, m.index));
    }
    const fullMatch = m[0];
    const tagName = m[1].toLowerCase();
    const arg = m[2] || null;
    // Is it a closing tag we expect?
    if (/^\[\//.test(slice.slice(m.index))) {
      // We never match closing via TAG_OPEN regex; ignore.
      stack[stack.length - 1].children.push(fullMatch);
      i += m.index + fullMatch.length;
      continue;
    }
    // Look ahead for the closing tag
    const closeRe = TAG_CLOSE_OF(tagName);
    const afterOpen = slice.slice(m.index + fullMatch.length);
    const close = afterOpen.match(closeRe);
    if (!close) {
      // No close tag found → treat as literal
      stack[stack.length - 1].children.push(fullMatch);
      i += m.index + fullMatch.length;
      continue;
    }
    // Recurse into the inner text
    const inner = afterOpen.slice(0, close.index);
    const node = { tag: tagName, arg, children: parseRecursive(inner) };
    stack[stack.length - 1].children.push(node);
    i += m.index + fullMatch.length + close.index + close[0].length;
  }
  return root.children;
}

function parseRecursive(text) { return parse(text); }

/** Render BBCode text to React. */
export default function renderBBCode(text) {
  if (!text) return null;
  const nodes = parse(text);
  return nodes.map((n, i) => (
    <React.Fragment key={i}>{renderNode(n, `n${i}`)}</React.Fragment>
  ));
}
